// src/pages/Enterprise.jsx
import { useState } from 'react'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'
import { submitContact } from '../api/services'

// -- Data --------------------------------------------------------------

const BENEFITS = [
    {
        title: 'Consistent risk standards across your team',
        body: 'Every team member sends communications screened against the same standard, regardless of seniority or department. No more relying on individual judgement for high-stakes messages.',
        icon: '📊',
    },
    {
        title: 'Fewer escalations, fewer disputes',
        body: 'Messages that would have triggered a grievance, a legal letter, or a client walkout get caught and rewritten before they are sent.',
        icon: '🛡️',
    },
    {
        title: 'A clear record for compliance and audit',
        body: 'Risk tier, score, and context are logged for every analysis your team runs — without ever storing the message itself. Useful evidence of due diligence, with privacy fully intact.',
        icon: '📋',
    },
    {
        title: 'Built for the way your organisation works',
        body: 'Volume licensing, centralised billing, and admin oversight across your whole team, so it scales the way your organisation already operates.',
        icon: '🏢',
    },
]

const STEPS = [
    {
        title: 'Tell us about your team',
        body: 'Share your team size and what you need GMC to cover. We will recommend the right plan for your organisation.',
    },
    {
        title: 'We set up your account',
        body: 'Your organisation account is provisioned with team licences ready to assign, and centralised billing for your finance team.',
    },
    {
        title: 'Your team gets started',
        body: 'Team members are added to your account and can begin analysing communications immediately, with usage visible to your admin.',
    },
]

const ENTERPRISE_FEATURES = [
    'Volume licensing with flexible seat allocation',
    'Centralised billing and consolidated invoicing',
    'Admin dashboard for team oversight and usage monitoring',
    'Dedicated account management and onboarding support',
    'Custom integration options (API access available)',
    'Priority support with guaranteed response times',
    'Advanced security and compliance features',
    'Custom training and rollout support for your organisation',
]

// -- Page ----------------------------------------------------------------

export default function Enterprise() {
    const [form, setForm] = useState({
        name: '',
        email: '',
        company: '',
        message: '',
    })
    const [submitting, setSubmitting] = useState(false)
    const [submitted, setSubmitted] = useState(false)
    const [ticketCode, setTicketCode] = useState(null)
    const [error, setError] = useState('')

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value })
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        setError('')
        setSubmitting(true)
        try {
            const res = await submitContact({ ...form, category: 'enterprise' })
            setTicketCode(res.data?.ticket_code || null)
            setSubmitted(true)
        } catch (err) {
            const detail = err.response?.data?.detail
            if (Array.isArray(detail)) {
                setError(detail.map((d) => d.msg).join('. '))
            } else if (typeof detail === 'string') {
                setError(detail)
            } else {
                setError('We could not send your message. Please try again or email us directly.')
            }
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <div className="min-h-screen flex flex-col bg-[#07090e] text-slate-100 font-body antialiased relative overflow-x-hidden selection:bg-brand-amber/30 selection:text-white">

            {/* Ambient Background Radials */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[350px] bg-gradient-to-b from-indigo-500/5 to-transparent blur-[120px] pointer-events-none" />
            <div className="absolute bottom-0 left-[-10%] w-[500px] h-[500px] bg-brand-amber/[0.02] blur-[130px] pointer-events-none rounded-full" />

            <Navbar />

            {/* -- Hero ----------------------------------------------- */}
            <section className="px-4 sm:px-6 pt-20 sm:pt-28 pb-16 relative z-10">
                <div className="max-w-4xl mx-auto text-center">
                    <div className="inline-flex items-center gap-2.5 px-5 py-2 bg-brand-amber/[0.06] border border-brand-amber/25 rounded-full shadow-inner backdrop-blur-md mb-7">
                        <span className="relative flex h-2.5 w-2.5">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-amber opacity-60" style={{ animationDuration: '2.5s' }} />
                            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-brand-amber" />
                        </span>
                        <span className="text-sm font-sans font-semibold tracking-widest uppercase text-brand-amber">
                            Enterprise
                        </span>
                    </div>

                    <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-6 leading-tight">
                        Enterprise-grade communication <br className="hidden sm:block" /> risk protection for your whole team.
                    </h1>
                    <p className="text-base font-body text-slate-400 font-light leading-relaxed max-w-2xl mx-auto">
                        GMC for Enterprise brings communication risk screening to your entire
                        organisation, with centralised billing, team-wide licensing, and the same
                        zero-retention guarantee your business already relies on.
                    </p>
                </div>
            </section>

            {/* -- Benefits ---------------------------------------------- */}
            <section className="px-4 sm:px-6 pb-20 relative z-10">
                <div className="max-w-5xl mx-auto">
                    <p className="text-xs font-sans font-semibold tracking-widest uppercase text-slate-400 text-center mb-10">
                        Why organisations choose GMC Enterprise
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        {BENEFITS.map(b => (
                            <div key={b.title}
                                className="rounded-xl p-6 bg-white/[0.01] border border-white/[0.05] hover:bg-white/[0.02] transition-all duration-300 backdrop-blur-sm">
                                <div className="text-2xl mb-3">{b.icon}</div>
                                <h3 className="text-base font-sans font-semibold text-white mb-2">
                                    {b.title}
                                </h3>
                                <p className="text-base font-body font-light text-slate-400 leading-relaxed">
                                    {b.body}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* -- Enterprise Features ------------------------------------- */}
            <section className="px-4 sm:px-6 pb-20 relative z-10">
                <div className="max-w-4xl mx-auto">
                    <div className="rounded-2xl bg-white/[0.01] border border-white/[0.05] p-8 sm:p-10 backdrop-blur-sm shadow-xl">
                        <h2 className="text-xl font-sans font-semibold text-white mb-6 text-center">
                            Everything you need to scale communication risk protection
                        </h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {ENTERPRISE_FEATURES.map((feature, i) => (
                                <div key={i} className="flex items-start gap-3 p-2.5 rounded-lg hover:bg-white/[0.02] transition-colors">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                        stroke="#D4A843" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 mt-0.5">
                                        <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                    <span className="text-base font-body font-light text-slate-300">{feature}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* -- How it works -------------------------------------------- */}
            <section className="px-4 sm:px-6 pb-20 relative z-10">
                <div className="max-w-3xl mx-auto">
                    <p className="text-xs font-sans font-semibold tracking-widest uppercase text-slate-400 text-center mb-10">
                        How it works
                    </p>
                    <div className="space-y-8">
                        {STEPS.map((step, i) => (
                            <div key={step.title} className="flex gap-5 group">
                                <div className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center font-sans font-bold text-sm bg-brand-amber/10 text-brand-amber border border-brand-amber/25 group-hover:bg-brand-amber/20 transition-colors">
                                    {i + 1}
                                </div>
                                <div className="pt-0.5">
                                    <h4 className="text-base font-sans font-semibold text-white mb-1.5">
                                        {step.title}
                                    </h4>
                                    <p className="text-base font-body font-light text-slate-400 leading-relaxed">
                                        {step.body}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* -- Contact form -------------------------------------------- */}
            <section className="px-4 sm:px-6 pb-24 relative z-10">
                <div className="max-w-xl mx-auto">
                    <div className="rounded-2xl overflow-hidden bg-white/[0.01] border border-white/[0.05] backdrop-blur-sm shadow-xl">

                        <div className="px-7 pt-7 pb-5 border-b border-white/[0.06]">
                            <h2 className="font-sans font-bold text-xl text-white mb-1.5">
                                Talk to our enterprise team
                            </h2>
                            <p className="text-base font-body font-light text-slate-400">
                                Tell us a little about your organisation and we will get back to you
                                within one business day.
                            </p>
                        </div>

                        {submitted ? (
                            <div className="px-7 py-10 text-center">
                                <div className="w-12 h-12 rounded-full mx-auto mb-4 flex items-center justify-center bg-emerald-500/10 border border-emerald-500/25">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                                        stroke="#10B981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                </div>
                                <p className="text-base font-sans font-semibold text-white mb-1">
                                    Thank you — we have received your message
                                </p>
                                <p className="text-base font-body font-light text-slate-400">
                                    A member of our enterprise team will be in touch shortly.
                                </p>
                                {ticketCode && (
                                    <div className="mt-5 inline-flex flex-col items-center gap-1 px-6 py-4 rounded-xl bg-brand-amber/[0.06] border border-brand-amber/20">
                                        <span className="text-xs font-sans font-semibold tracking-widest uppercase text-slate-400">
                                            Your reference
                                        </span>
                                        <span className="text-xl font-mono font-bold text-brand-amber tracking-wide">
                                            {ticketCode}
                                        </span>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="px-7 py-6 space-y-5">
                                <div>
                                    <label className="block text-xs font-sans font-semibold tracking-widest uppercase text-slate-400 mb-2">
                                        Your name
                                    </label>
                                    <input
                                        type="text" name="name" required
                                        value={form.name} onChange={handleChange}
                                        placeholder="Full name"
                                        className="w-full px-4 py-3 rounded-lg text-sm text-white placeholder-slate-500 bg-white/[0.04] border border-white/[0.08] outline-none focus:border-brand-amber/40 transition-colors"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-sans font-semibold tracking-widest uppercase text-slate-400 mb-2">
                                        Work email
                                    </label>
                                    <input
                                        type="email" name="email" required
                                        value={form.email} onChange={handleChange}
                                        placeholder="Email address"
                                        className="w-full px-4 py-3 rounded-lg text-sm text-white placeholder-slate-500 bg-white/[0.04] border border-white/[0.08] outline-none focus:border-brand-amber/40 transition-colors"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-sans font-semibold tracking-widest uppercase text-slate-400 mb-2">
                                        Company
                                    </label>
                                    <input
                                        type="text" name="company" required
                                        value={form.company} onChange={handleChange}
                                        placeholder="Company Name"
                                        className="w-full px-4 py-3 rounded-lg text-sm text-white placeholder-slate-500 bg-white/[0.04] border border-white/[0.08] outline-none focus:border-brand-amber/40 transition-colors"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-sans font-semibold tracking-widest uppercase text-slate-400 mb-2">
                                        Tell us about your team
                                    </label>
                                    <textarea
                                        name="message" required rows={4}
                                        value={form.message} onChange={handleChange}
                                        placeholder="Team size, what you're hoping to achieve, any specific requirements"
                                        className="w-full px-4 py-3 rounded-lg text-sm text-white placeholder-slate-500 bg-white/[0.04] border border-white/[0.08] outline-none focus:border-brand-amber/40 transition-colors resize-none"
                                    />
                                </div>

                                {error && (
                                    <p className="text-sm font-body text-rose-400 leading-relaxed">{error}</p>
                                )}

                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="w-full py-3.5 rounded-lg font-sans font-bold text-sm transition-all duration-200 flex items-center justify-center gap-2 bg-brand-amber hover:bg-brand-amber2 text-[#07090e] disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {submitting ? (
                                        <>
                                            <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                                <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10-4.477-10-10-10z" strokeOpacity="0.3" />
                                                <path d="M12 2a10 10 0 0 1 10 10" />
                                            </svg>
                                            Sending...
                                        </>
                                    ) : (
                                        'Get in touch'
                                    )}
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    )
}